import React, { useState, useMemo } from "react";
import { languages, domainSubjects, generalTest } from "./data/subjects";

const SubjectSelector = ({ selectedSubjects, onToggleSubject }) => {
    const [showLangDropdown, setShowLangDropdown] = useState(false);
    const [showDomainDropdown, setShowDomainDropdown] = useState(null); // index of which domain slot to fill
    const [langSearch, setLangSearch] = useState("");
    const [domainSearch, setDomainSearch] = useState("");

    // Categorize selected subjects
    const selectedLanguages = useMemo(
        () => selectedSubjects.filter((s) => languages.includes(s)),
        [selectedSubjects]
    );
    const selectedDomains = useMemo(
        () => selectedSubjects.filter((s) => domainSubjects.includes(s)),
        [selectedSubjects]
    );
    const hasGeneralTest = useMemo(
        () => selectedSubjects.some((s) => generalTest.includes(s)),
        [selectedSubjects]
    );

    // Domain slot count — at least 3 slots, or however many are selected + 1
    const domainSlotCount = Math.max(3, selectedDomains.length + 1);

    const filteredLanguages = languages.filter(
        (l) =>
            !selectedLanguages.includes(l) &&
            (!langSearch || l.toLowerCase().includes(langSearch.toLowerCase()))
    );

    const getAvailableDomains = () =>
        domainSubjects.filter(
            (d) =>
                !selectedDomains.includes(d) &&
                (!domainSearch || d.toLowerCase().includes(domainSearch.toLowerCase()))
        );

    const handleSelectLanguage = (lang) => {
        onToggleSubject(lang);
        setShowLangDropdown(false);
        setLangSearch("");
    };

    const handleSelectDomain = (domain) => {
        onToggleSubject(domain);
        setShowDomainDropdown(null);
        setDomainSearch("");
    };

    const handleToggleGeneralTest = () => {
        onToggleSubject(generalTest[0]);
    };

    return (
        <div className="elig-sections">
            {/* SECTION 1: Language */}
            <div className="elig-section-card">
                <div className="elig-section-card__header">
                    <div className="elig-section-icon elig-section-icon--lang">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 8l6 6M4 14l6-6 2-3M2 5h12M7 2h1M22 22l-5-10-5 10M14 18h8" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="elig-section-card__title">Language (Section 1)</h3>
                        <p className="elig-section-card__subtitle">Pick up to 2 languages from Section IA & IB</p>
                    </div>
                </div>
                <div className="elig-section-card__body">
                    <div className="elig-lang-chips">
                        {selectedLanguages.map((lang) => (
                            <span key={lang} className="elig-chip elig-chip--filled">
                                {lang}
                                <button
                                    className="elig-chip__remove"
                                    onClick={() => onToggleSubject(lang)}
                                    aria-label={`Remove ${lang}`}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {selectedLanguages.length < 2 && (
                            <div className="elig-dropdown-wrapper">
                                <button
                                    className="elig-chip elig-chip--add"
                                    onClick={() => setShowLangDropdown(!showLangDropdown)}
                                >
                                    <span>+</span> Add Language
                                </button>
                                {showLangDropdown && (
                                    <div className="elig-dropdown">
                                        <input
                                            type="text"
                                            className="elig-dropdown__search"
                                            placeholder="Search languages..."
                                            value={langSearch}
                                            onChange={(e) => setLangSearch(e.target.value)}
                                            autoFocus
                                        />
                                        <div className="elig-dropdown__list">
                                            {filteredLanguages.map((lang) => (
                                                <button
                                                    key={lang}
                                                    className="elig-dropdown__item"
                                                    onClick={() => handleSelectLanguage(lang)}
                                                >
                                                    {lang}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SECTION 2: Domain Subjects */}
            <div className="elig-section-card">
                <div className="elig-section-card__header">
                    <div className="elig-section-icon elig-section-icon--domain">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="3" width="20" height="14" rx="2" />
                            <path d="M8 21h8M12 17v4" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="elig-section-card__title">Domain Subjects (Section 2)</h3>
                        <p className="elig-section-card__subtitle">Select at least 3 subjects relevant to your preferred degree</p>
                    </div>
                </div>
                <div className="elig-section-card__body">
                    <div className="elig-domain-grid">
                        {Array.from({ length: domainSlotCount }).map((_, idx) => {
                            const selected = selectedDomains[idx];
                            return (
                                <div key={idx} className="elig-domain-slot">
                                    <label className="elig-domain-slot__label">DOMAIN SUBJECT {idx + 1}</label>
                                    {selected ? (
                                        <div className="elig-select elig-select--filled">
                                            <span>{selected}</span>
                                            <button
                                                className="elig-select__clear"
                                                onClick={() => onToggleSubject(selected)}
                                                aria-label={`Remove ${selected}`}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="elig-dropdown-wrapper">
                                            <button
                                                className="elig-select elig-select--empty"
                                                onClick={() =>
                                                    setShowDomainDropdown(
                                                        showDomainDropdown === idx ? null : idx
                                                    )
                                                }
                                            >
                                                <span>Select subject...</span>
                                                <span className="elig-select__chevron">▾</span>
                                            </button>
                                            {showDomainDropdown === idx && (
                                                <div className="elig-dropdown">
                                                    <input
                                                        type="text"
                                                        className="elig-dropdown__search"
                                                        placeholder="Search domains..."
                                                        value={domainSearch}
                                                        onChange={(e) =>
                                                            setDomainSearch(e.target.value)
                                                        }
                                                        autoFocus
                                                    />
                                                    <div className="elig-dropdown__list">
                                                        {getAvailableDomains().map((d) => (
                                                            <button
                                                                key={d}
                                                                className="elig-dropdown__item"
                                                                onClick={() => handleSelectDomain(d)}
                                                            >
                                                                {d}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {selectedDomains.length >= domainSlotCount && selectedDomains.length < 6 && (
                            <div className="elig-domain-slot">
                                <label className="elig-domain-slot__label">&nbsp;</label>
                                <button
                                    className="elig-select elig-select--add-more"
                                    onClick={() => setShowDomainDropdown(domainSlotCount)}
                                >
                                    <span>⊕</span> Add More Domains
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* SECTION 3: General Test */}
            <div className="elig-section-card">
                <div className="elig-section-card__header">
                    <div className="elig-section-icon elig-section-icon--general">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01" />
                        </svg>
                    </div>
                    <div className="elig-section-card__header-text">
                        <h3 className="elig-section-card__title">General Test (Section 3)</h3>
                        <p className="elig-section-card__subtitle">Required for many vocational and professional courses</p>
                    </div>
                    <div className="elig-toggle-group">
                        <span className="elig-toggle-label">Opting?</span>
                        <button
                            className={`elig-toggle ${hasGeneralTest ? "elig-toggle--on" : ""}`}
                            onClick={handleToggleGeneralTest}
                            role="switch"
                            aria-checked={hasGeneralTest}
                        >
                            <span className="elig-toggle__thumb" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary bar */}
            {selectedSubjects.length > 0 && (
                <div className="elig-summary-bar">
                    <span className="elig-summary-bar__icon">ℹ️</span>
                    <span>
                        Your current selection:{" "}
                        <strong>
                            {selectedLanguages.length > 0
                                ? selectedLanguages.join(", ")
                                : "No languages"}
                            {selectedDomains.length > 0
                                ? ` + ${selectedDomains.length} Domain Subject${selectedDomains.length > 1 ? "s" : ""}`
                                : ""}
                            {hasGeneralTest ? " + General Test" : ""}
                        </strong>
                        . You can change these later to see different eligibility results.
                    </span>
                </div>
            )}
        </div>
    );
};

export default SubjectSelector;
